import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import type {
    AmountBreakdown,
    PaymentRequestDeliveryEventView,
    PaymentRequestDetailsView,
    PaymentRequestDocumentDescriptor,
    MerchantTimeZone,
    LosslessNumber,
} from '@pillar-portal/api-types';
import {
    formatLosslessNumberFixed,
    formatLosslessUsd,
    formatTimestampForMerchantUi,
} from '@pillar-portal/api-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { BackButton } from '@/components/ui/back-button';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { paymentsApi } from '@/lib/pillarApi';
import { formatPaymentRequestSourceDocumentReference } from './source-document-reference';

function formatAmount(amount: LosslessNumber | undefined, currencyCode?: string): string {
    if (amount === undefined) {
        return '—';
    }
    if (currencyCode === 'USD') {
        return formatLosslessUsd(amount);
    }
    if (currencyCode === undefined) {
        return formatLosslessNumberFixed(amount, 2);
    }
    return `${formatLosslessNumberFixed(amount, 2)} ${currencyCode}`;
}

function formatDateTime(value: string | undefined, timeZone: MerchantTimeZone | null): string {
    if (value === undefined) {
        return '—';
    }
    return formatTimestampForMerchantUi(value, timeZone);
}

function formatBytes(value: number): string {
    if (value < 1024) {
        return `${value} B`;
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function renderBreakdownRows(
    amountBreakdown: AmountBreakdown | undefined,
    currencyCode: string
): ReadonlyArray<{
    label: string;
    value: string;
}> {
    if (amountBreakdown === undefined) {
        return [];
    }

    return [
        { label: 'Base amount', value: formatAmount(amountBreakdown.base_amount, currencyCode) },
        { label: 'Tax', value: formatAmount(amountBreakdown.tax, currencyCode) },
        { label: 'Surcharge', value: formatAmount(amountBreakdown.surcharge, currencyCode) },
        {
            label: 'Convenience fee',
            value: formatAmount(amountBreakdown.convenience_fee, currencyCode),
        },
        { label: 'Donation', value: formatAmount(amountBreakdown.donation, currencyCode) },
        { label: 'Total', value: formatAmount(amountBreakdown.total_amount, currencyCode) },
    ].filter((row) => row.value !== '—');
}

function DescriptionListRow(props: { label: string; value: string }) {
    return (
        <div className="grid grid-cols-[180px_1fr] gap-3 border-b py-2 last:border-b-0">
            <dt className="text-sm font-medium text-muted-foreground">{props.label}</dt>
            <dd className="text-sm">{props.value}</dd>
        </div>
    );
}

function DocumentRow(props: { document: PaymentRequestDocumentDescriptor }) {
    const { document } = props;
    return (
        <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <p className="font-medium">
                        {document.original_filename ?? 'Generated document'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {document.source_kind} · {document.media_type} ·{' '}
                        {formatBytes(document.content_length_bytes)}
                    </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                    SHA-256: {document.content_sha256.slice(0, 12)}...
                </div>
            </div>
        </div>
    );
}

function DeliveryEventRow(props: {
    deliveryEvent: PaymentRequestDeliveryEventView;
    merchantTimeZone: MerchantTimeZone | null;
}) {
    const { deliveryEvent } = props;
    return (
        <div
            className="rounded-lg border p-4"
            data-payment-request-delivery-event-uuid={
                deliveryEvent.payment_request_delivery_event_uuid
            }
        >
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <p className="font-medium">{deliveryEvent.provider_event_type}</p>
                    <p className="text-sm text-muted-foreground">
                        {deliveryEvent.delivery_channel} via {deliveryEvent.delivery_provider}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Message ID: {deliveryEvent.provider_message_id ?? '—'}
                    </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                    <p>
                        Received:{' '}
                        {formatDateTime(deliveryEvent.received_ts, props.merchantTimeZone)}
                    </p>
                    <p>
                        Provider:{' '}
                        {formatDateTime(deliveryEvent.provider_event_ts, props.merchantTimeZone)}
                    </p>
                </div>
            </div>
        </div>
    );
}

function PaymentTransactionRow(props: {
    paymentTransaction: PaymentRequestDetailsView['payment_transactions'][number];
    merchantTimeZone: MerchantTimeZone | null;
}) {
    const { paymentTransaction } = props;
    const title =
        paymentTransaction.merchant_transaction_id ?? paymentTransaction.payment_transaction_uuid;

    return (
        <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    {paymentTransaction.can_display ? (
                        <Link
                            className="font-medium text-primary hover:underline"
                            to={`/payment-transactions/${paymentTransaction.payment_transaction_uuid}`}
                        >
                            {title}
                        </Link>
                    ) : (
                        <p className="font-medium">{title}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                        {formatAmount(
                            paymentTransaction.original_amount,
                            paymentTransaction.currency_code
                        )}{' '}
                        · {paymentTransaction.status}
                    </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                    <p>
                        Created:{' '}
                        {formatDateTime(paymentTransaction.created_ts, props.merchantTimeZone)}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function PaymentRequestDetailView(props: {
    paymentRequest: PaymentRequestDetailsView;
    merchantOrganizationUuid: string;
    operationalRoleAssignmentUuid: string | undefined;
    pageDescription: string;
    backHref: string;
    backTooltip: string;
    actions?: ReactNode;
    showDeliveryEvents?: boolean;
    showSupportDetails?: boolean;
    showPaymentTransactions?: boolean;
}) {
    const paymentRequest = props.paymentRequest;
    const showDeliveryEvents: boolean = props.showDeliveryEvents ?? true;
    const showPaymentTransactions: boolean = props.showPaymentTransactions ?? false;
    const formattedSourceDocumentReference = formatPaymentRequestSourceDocumentReference({
        sourceDocumentReference: paymentRequest.source_document_reference,
        sourceDocumentKey: paymentRequest.source_document_key,
    });
    const breakdownRows = renderBreakdownRows(
        paymentRequest.amount_breakdown,
        paymentRequest.currency_code
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h1 className="text-3xl font-bold">
                        {paymentRequest.title ??
                            formattedSourceDocumentReference ??
                            'Payment Request'}
                    </h1>
                    <p className="text-sm text-muted-foreground">{props.pageDescription}</p>
                </div>
                <div className="flex items-center gap-3">
                    {props.actions}
                    <BackButton href={props.backHref} tooltip={props.backTooltip} />
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                        Summary
                        <StatusBadge status={paymentRequest.status} />
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <dl>
                        <DescriptionListRow
                            label="Reference"
                            value={formattedSourceDocumentReference ?? '—'}
                        />
                        <DescriptionListRow
                            label="Customer name"
                            value={paymentRequest.payment_request_details?.customerName ?? '—'}
                        />
                        <DescriptionListRow
                            label="Original amount"
                            value={formatAmount(
                                paymentRequest.original_amount,
                                paymentRequest.currency_code
                            )}
                        />
                        <DescriptionListRow label="Currency" value={paymentRequest.currency_code} />
                        <DescriptionListRow
                            label="Recipient email"
                            value={paymentRequest.recipient_email ?? '—'}
                        />
                        <DescriptionListRow
                            label="Recipient phone"
                            value={paymentRequest.recipient_phone ?? '—'}
                        />
                        <DescriptionListRow
                            label="Virtual terminal"
                            value={
                                paymentRequest.virtual_terminal?.name ??
                                (paymentRequest.virtual_terminal === undefined
                                    ? 'No VT assigned'
                                    : 'Unnamed virtual terminal')
                            }
                        />
                        <DescriptionListRow
                            label="Virtual terminal ID"
                            value={paymentRequest.virtual_terminal?.processor_device_id ?? '—'}
                        />
                        <DescriptionListRow
                            label="Created"
                            value={formatDateTime(
                                paymentRequest.created_ts,
                                paymentRequest.merchant_timezone
                            )}
                        />
                        <DescriptionListRow
                            label="Expires"
                            value={formatDateTime(
                                paymentRequest.expires_ts,
                                paymentRequest.merchant_timezone
                            )}
                        />
                        <DescriptionListRow label="Message" value={paymentRequest.message ?? '—'} />
                    </dl>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Amount Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                    {breakdownRows.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No amount breakdown is available for this payment request.
                        </p>
                    ) : (
                        <dl>
                            {breakdownRows.map((row) => (
                                <DescriptionListRow
                                    key={row.label}
                                    label={row.label}
                                    value={row.value}
                                />
                            ))}
                        </dl>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Documents</CardTitle>
                </CardHeader>
                <CardContent>
                    {paymentRequest.documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No documents are attached to this payment request.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {paymentRequest.documents.map((document) => (
                                <DocumentRow
                                    key={document.payment_request_document_uuid}
                                    document={document}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {showDeliveryEvents && (
                <InfiniteScrollList<PaymentRequestDeliveryEventView, string, readonly unknown[]>
                    pageTitle="Delivery Events"
                    cardTitle="Delivery Events"
                    queryPlaceholder=""
                    showQueryInput={false}
                    showPageHeader={false}
                    queryKeyPrefix={[
                        'paymentRequestDeliveryEvents',
                        paymentRequest.payment_request_uuid,
                        props.merchantOrganizationUuid,
                        props.operationalRoleAssignmentUuid,
                    ]}
                    enabled={props.operationalRoleAssignmentUuid !== undefined}
                    pageSize={50}
                    retry={false}
                    stateCopy={{
                        emptyLabel: 'No delivery events have been recorded yet.',
                        genericErrorLabel: 'Failed to load delivery events.',
                    }}
                    parseCursorParam={(pageParam: unknown) =>
                        z.string().uuid().nullish().parse(pageParam) ?? undefined
                    }
                    fetchPage={async ({ limit, cursor }) => {
                        const paymentRequestDeliveryEventList =
                            await paymentsApi.getMerchantPaymentRequestDeliveryEvents(
                                paymentRequest.payment_request_uuid,
                                {
                                    contract_version: 'v1',
                                    limit,
                                    cursor,
                                    merchant_organization_uuid: props.merchantOrganizationUuid,
                                }
                            );
                        return {
                            items: paymentRequestDeliveryEventList.delivery_events,
                            pagination: paymentRequestDeliveryEventList.pagination,
                        };
                    }}
                    renderItems={({ items }) => (
                        <div className="space-y-3">
                            {items.map((deliveryEvent) => (
                                <DeliveryEventRow
                                    key={deliveryEvent.payment_request_delivery_event_uuid}
                                    deliveryEvent={deliveryEvent}
                                    merchantTimeZone={paymentRequest.merchant_timezone}
                                />
                            ))}
                        </div>
                    )}
                />
            )}

            {showPaymentTransactions && (
                <Card>
                    <CardHeader>
                        <CardTitle>Payment Transactions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {paymentRequest.payment_transactions.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No payment transactions are visible for this payment request.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {paymentRequest.payment_transactions.map((paymentTransaction) => (
                                    <PaymentTransactionRow
                                        key={paymentTransaction.payment_transaction_uuid}
                                        paymentTransaction={paymentTransaction}
                                        merchantTimeZone={paymentRequest.merchant_timezone}
                                    />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {props.showSupportDetails === true && (
                <Card>
                    <CardHeader>
                        <CardTitle>Support Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <dl>
                            <DescriptionListRow
                                label="Reconciliation"
                                value={paymentRequest.reconciled ?? '—'}
                            />
                        </dl>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
